import { BodyBatteryDay, DailyHealth, HealthResource, HealthRange, HrvDay, SleepNight } from '../api/types';
import { addDaysIso, daysBetween, isoDaysAgo } from './format';

/**
 * Metric definitions for the history pages (/health/:metric). Each metric reads one /health resource and
 * turns a row into named values. The first field is the headline value: it drives the stats and the trend.
 */

export const METRIC_KEYS = ['hrv', 'resting-hr', 'sleep', 'body-battery', 'stress', 'steps'] as const;
export type MetricKey = (typeof METRIC_KEYS)[number];

/** Values of one day. null means Garmin did not return the field for that day. */
export type DayValues = Record<string, number | null>;

export interface MetricField {
  key: string;
  label: string;
  unit?: string;
  digits?: number;
  /** Set when the field is drawn in the chart. */
  plot?: { color: string; dashed?: boolean };
}

export interface MetricDef {
  key: MetricKey;
  title: string;
  description: string;
  resource: HealthResource;
  chart: 'line' | 'bar';
  fields: MetricField[];
  /** Fixed y-axis domain. Other metrics use the data range. */
  domain?: [number | string, number | string];
  /** Maps a raw row from the API to the fields above. */
  // The row type differs per metric (DailyHealth, SleepNight, ...). Each definition below types its own row.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: (row: any) => DayValues;
}

const hours = (seconds: number | null) => (seconds == null ? null : Number((seconds / 3600).toFixed(2)));

export const METRICS: Record<MetricKey, MetricDef> = {
  hrv: {
    key: 'hrv',
    title: 'HRV',
    description: 'Heart rate variability (ms). The headline value is last night; the dashed line is the 7-day average.',
    resource: 'hrv',
    chart: 'line',
    domain: ['dataMin - 5', 'dataMax + 5'],
    fields: [
      { key: 'lastNight', label: 'Last night', unit: 'ms', digits: 0, plot: { color: 'var(--chart-1)' } },
      { key: 'weekly', label: '7-day avg', unit: 'ms', digits: 0, plot: { color: 'var(--chart-3)', dashed: true } },
    ],
    values: (row: HrvDay) => ({ lastNight: row.lastNightAvg, weekly: row.weeklyAvg }),
  },
  'resting-hr': {
    key: 'resting-hr',
    title: 'Resting heart rate',
    description: 'Resting heart rate (bpm), one value per day.',
    resource: 'daily',
    chart: 'line',
    domain: ['dataMin - 3', 'dataMax + 3'],
    fields: [{ key: 'bpm', label: 'Resting HR', unit: 'bpm', digits: 0, plot: { color: 'var(--chart-4)' } }],
    values: (row: DailyHealth) => ({ bpm: row.restingHr }),
  },
  sleep: {
    key: 'sleep',
    title: 'Sleep',
    description: 'Total sleep per night (hours) and the Garmin sleep score.',
    resource: 'sleep',
    chart: 'bar',
    domain: [0, 'dataMax + 1'],
    fields: [
      { key: 'hours', label: 'Hours asleep', unit: 'h', digits: 1, plot: { color: 'var(--chart-2)' } },
      { key: 'score', label: 'Sleep score', digits: 0 },
      { key: 'deep', label: 'Deep', unit: 'h', digits: 1 },
      { key: 'rem', label: 'REM', unit: 'h', digits: 1 },
    ],
    values: (row: SleepNight) => ({
      hours: hours(row.totalSleepS),
      score: row.sleepScore,
      deep: hours(row.deepSleepS),
      rem: hours(row.remSleepS),
    }),
  },
  'body-battery': {
    key: 'body-battery',
    title: 'Body battery',
    description: 'Highest and lowest body battery of each day (0 to 100).',
    resource: 'body-battery',
    chart: 'line',
    domain: [0, 100],
    fields: [
      { key: 'high', label: 'Highest', digits: 0, plot: { color: 'var(--chart-1)' } },
      { key: 'low', label: 'Lowest', digits: 0, plot: { color: 'var(--chart-2)' } },
    ],
    values: (row: BodyBatteryDay) => ({ high: row.highest, low: row.lowest }),
  },
  stress: {
    key: 'stress',
    title: 'Stress',
    description: 'Average stress for the day (Garmin scale, 0 to 100).',
    resource: 'daily',
    chart: 'line',
    domain: [0, 100],
    fields: [{ key: 'avg', label: 'Average stress', digits: 0, plot: { color: 'var(--chart-3)' } }],
    values: (row: DailyHealth) => ({ avg: row.stressAvg }),
  },
  steps: {
    key: 'steps',
    title: 'Steps',
    description: 'Steps per day.',
    resource: 'daily',
    chart: 'bar',
    domain: [0, 'auto'],
    fields: [{ key: 'steps', label: 'Steps', digits: 0, plot: { color: 'var(--chart-1)' } }],
    values: (row: DailyHealth) => ({ steps: row.steps }),
  },
};

export function isMetricKey(value: string): value is MetricKey {
  return (METRIC_KEYS as readonly string[]).includes(value);
}

/** Link to a metric's history, optionally for a given range (the Health page passes its own range). */
export function historyPath(key: MetricKey, range?: HealthRange): string {
  return range ? `/health/${key}?from=${range.from}&to=${range.to}` : `/health/${key}`;
}

// ---- ranges ------------------------------------------------------------------

/** Presets offered by the range selector. days is inclusive of today. */
export const RANGE_PRESETS = [
  { key: '14', label: '14 days', days: 14 },
  { key: '30', label: '30 days', days: 30 },
  { key: '90', label: '90 days', days: 90 },
  { key: '182', label: '6 months', days: 182 },
  { key: '365', label: '1 year', days: 365 },
] as const;

/** The backend refuses ranges longer than this (inclusive days). */
export const MAX_RANGE_DAYS = 366;

export const DEFAULT_PRESET = '30';

export interface Period {
  from: string;
  to: string;
  /** Inclusive day count. */
  days: number;
  /** The equal-length period before this one, used for the trend. */
  previous: HealthRange;
  /** Preset key, or 'custom'. */
  preset: string;
}

export function periodFor(from: string, to: string, preset: string): Period {
  const days = daysBetween(from, to) + 1;
  return {
    from,
    to,
    days,
    previous: { from: addDaysIso(from, -days), to: addDaysIso(from, -1) },
    preset,
  };
}

export function presetPeriod(key: string): Period {
  const preset = RANGE_PRESETS.find(p => p.key === key) ?? RANGE_PRESETS[1];
  return periodFor(isoDaysAgo(preset.days - 1), isoDaysAgo(0), preset.key);
}

/** Checks a custom range. Returns an error message for the user, or null when it is usable. */
export function customRangeError(from: string, to: string, today: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return 'Enter both dates.';
  if (from > to) return 'The start date is after the end date.';
  if (to > today) return 'The end date is in the future.';
  if (daysBetween(from, to) + 1 > MAX_RANGE_DAYS) return `A range can be at most ${MAX_RANGE_DAYS} days.`;
  return null;
}

// ---- values and stats ---------------------------------------------------------

export interface DayPoint {
  date: string;
  values: DayValues;
}

/** Rows from the API (oldest first) to day points for one metric. */
export function toPoints(metric: MetricDef, rows: unknown[]): DayPoint[] {
  return rows.map(row => {
    const r = row as { date: string };
    return { date: r.date, values: metric.values(row) };
  });
}

export interface RangeStats {
  /** Days the API returned for the range. */
  fetched: number;
  /** Days where the headline value is present. */
  withValue: number;
  latest: { date: string; value: number } | null;
  average: number | null;
  min: number | null;
  max: number | null;
}

const round = (value: number, digits: number) => Number(value.toFixed(digits));

export function summarize(points: DayPoint[], field: string, digits = 0): RangeStats {
  const valued = points.filter(p => p.values[field] != null);
  const numbers = valued.map(p => p.values[field] as number);
  const sum = numbers.reduce((total, v) => total + v, 0);
  return {
    fetched: points.length,
    withValue: valued.length,
    latest: valued.length ? { date: valued[valued.length - 1].date, value: valued[valued.length - 1].values[field] as number } : null,
    average: numbers.length ? round(sum / numbers.length, digits) : null,
    min: numbers.length ? Math.min(...numbers) : null,
    max: numbers.length ? Math.max(...numbers) : null,
  };
}

export interface Trend {
  delta: number;
  percent: number | null;
}

/** Change of the period average against the previous period. null when either period has no values. */
export function trendOf(current: number | null, previous: number | null, digits = 0): Trend | null {
  if (current == null || previous == null) return null;
  const delta = round(current - previous, digits);
  return { delta, percent: previous === 0 ? null : Math.round((delta / previous) * 100) };
}
